import React, { useState, useEffect, useCallback } from "react";
import { studentLiveObserver } from "../../../realtime/services/StudentLiveObserver";
import { webrtcService } from "../../../realtime/services/WebRTCService";
import "./TeacherLaserOverlay.css";

const TeacherLaserOverlay = () => {
    const [activeTeachers, setActiveTeachers] = useState([]);
    const [laserPos, setLaserPos] = useState(null); // { x: 0.5, y: 0.5, teacherName: '' }
    const [activeTip, setActiveTip] = useState(null);
    const [activeHighlightBox, setActiveHighlightBox] = useState(null); // { x, y, width, height }
    const [isVoiceActive, setIsVoiceActive] = useState(false);

    const handleTeacherAnnotation = useCallback((annotation) => {
        if (!annotation) return;

        if (annotation.type === "LASER" || annotation.type === "LASER_MOVE") {
            if (annotation.active === false || annotation.type === "LASER_HIDE") {
                setLaserPos(null);
            } else {
                setLaserPos({
                    x: annotation.x,
                    y: annotation.y,
                    teacherName: annotation.teacherName || "المعلم"
                });
            }
        } else if (annotation.type === "LASER_HIDE") {
            setLaserPos(null);
        } else if (annotation.type === "HIGHLIGHT_BOX") {
            const box = annotation.box || annotation.rect;
            if (box && annotation.active !== false) {
                setActiveHighlightBox(box);
            } else {
                setActiveHighlightBox(null);
            }
        } else if (annotation.type === "CLEAR_DRAWINGS" || annotation.type === "CLEAR_ALL") {
            setActiveHighlightBox(null);
        } else if (annotation.type === "GUIDANCE_TIP") {
            setActiveTip({
                message: annotation.message,
                tipType: annotation.tipType || "info",
                teacherName: annotation.teacherName || "المعلم"
            });
            setTimeout(() => {
                setActiveTip(null);
            }, 6000);
        }
    }, []);

    useEffect(() => {
        studentLiveObserver.init();

        const unsubscribe = studentLiveObserver.subscribe((event, data) => {
            if (event === "TEACHER_CONNECTED") {
                setActiveTeachers((prev) => {
                    const filtered = prev.filter((t) => t.teacherConnectionId !== data.teacherConnectionId);
                    return [...filtered, data];
                });
            } else if (event === "TEACHER_DISCONNECTED") {
                setActiveTeachers((prev) => prev.filter((t) => t.teacherConnectionId !== data.teacherConnectionId));
                setLaserPos(null);
                setActiveHighlightBox(null);
            } else if (event === "TEACHER_ANNOTATION") {
                handleTeacherAnnotation(data);
            }
        });

        // Track voice call state
        webrtcService.onConnectionStateCallback = (state) => {
            setIsVoiceActive(state === "connected");
        };

        return () => {
            unsubscribe();
        };
    }, [handleTeacherAnnotation]);

    if (activeTeachers.length === 0 && !laserPos && !activeTip && !activeHighlightBox) {
        return null;
    }

    const currentTeacher = activeTeachers[0] || {};

    return (
        <div className="teacher-laser-overlay-root">
            {/* Glowing Laser Pointer */}
            {laserPos && (
                <div
                    className="teacher-live-laser-dot"
                    style={{
                        left: `${laserPos.x * 100}%`,
                        top: `${laserPos.y * 100}%`
                    }}
                >
                    <div className="laser-core"></div>
                    <div className="laser-pulse-ring"></div>
                    <div className="laser-pointer-label">
                        <i className="fal fa-chalkboard-teacher ml-1"></i>
                        <span>{laserPos.teacherName}</span>
                    </div>
                </div>
            )}

            {/* Glowing Highlight Focus Box */}
            {activeHighlightBox && (
                <div
                    className="teacher-highlight-focus-box animate__animated animate__pulse animate__infinite"
                    style={{
                        left: `${activeHighlightBox.x * 100}%`,
                        top: `${activeHighlightBox.y * 100}%`,
                        width: `${activeHighlightBox.width * 100}%`,
                        height: `${activeHighlightBox.height * 100}%`
                    }}
                >
                    <div className="highlight-tag">
                        <i className="fal fa-vector-square ml-1"></i>
                        <span>تحديد المعلم</span>
                    </div>
                </div>
            )}

            {/* Active Teacher Floating Pill Banner */}
            {activeTeachers.length > 0 && (
                <div className="teacher-connected-floating-pill animate__animated animate__fadeInDown">
                    <div className="teacher-pill-avatar-wrap">
                        <img
                            src={currentTeacher.teacherPhoto || "/assets/images/logo/130p150.png"}
                            alt={currentTeacher.teacherName}
                            className="teacher-pill-avatar"
                        />
                        <span className="teacher-pill-live-dot"></span>
                    </div>
                    <div className="teacher-pill-info">
                        <span className="teacher-pill-title">
                            <i className="fal fa-eye ml-1 text-primary"></i>
                            {currentTeacher.teacherName || "المعلم"} يتابع نشاطك الآن
                        </span>
                        {isVoiceActive && (
                            <span className="teacher-pill-voice-badge">
                                <span className="voice-wave-bar"></span>
                                <span className="voice-wave-bar"></span>
                                <span className="voice-wave-bar"></span>
                                <i className="fal fa-volume-up mr-1 text-success"></i>
                                <span>صوت مباشر نشط</span>
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Quick Guidance Tip Toast */}
            {activeTip && (
                <div className={`teacher-guidance-toast tip-${activeTip.tipType} animate__animated animate__bounceIn`}>
                    <div className="toast-icon">
                        <i className={activeTip.tipType === 'success' ? 'fas fa-star' : 'fas fa-lightbulb-on'}></i>
                    </div>
                    <div className="toast-body">
                        <span className="toast-teacher-badge">{activeTip.teacherName}</span>
                        <p className="toast-msg">{activeTip.message}</p>
                    </div>
                    <button
                        type="button"
                        className="toast-close-btn"
                        onClick={() => setActiveTip(null)}
                    >
                        <i className="fal fa-times"></i>
                    </button>
                </div>
            )}
        </div>
    );
};

export default TeacherLaserOverlay;
